import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  usePlmEventResponseByIdQuery,
  usePlmEventResponseEventsByIdsSubscription,
  EnumPlmEventResponseResponseStatus,
} from '../../../../types/generated-types';
import {
  Box,
  Button,
  Typography,
  Grid,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import {
  useInjectableComponents,
  BottomMenuItem,
} from '../../../system/services/injectableComponentsManager';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import BorderedSection from '../../shared/borderedSection';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { humanize } from '../../../ui/shell/utilities';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import '../ui/plm.css';

export const PlmEventResponseDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();

  const { data, loading, error } = usePlmEventResponseByIdQuery({
    variables: { _id: id! },
    skip: !id,
  });

  // Subscribe to real-time updates
  usePlmEventResponseEventsByIdsSubscription({
    variables: { _ids: [id!] },
    skip: !id,
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const plmEventResponse = data?.plmEventResponseById;

  useEffect(() => {
    if (error) {
      Notifier.error('Failed to load PLM Event Response details');
    }
  }, [error]);

  const isSmall = useIsSmallScreen();

  useEffect(() => {
    setTitle(
      isSmall ? 'PLM Event Response' : 'Peak Load Management Event Response',
    );
    setTitlePath('/plm-event-responses');
  }, [setTitle, setTitlePath, isSmall]);

  useEffect(() => {
    const menuItems: BottomMenuItem[] = [];

    if (can('update', 'PlmEventResponse')) {
      menuItems.push({
        id: 'edit-plm-event-response',
        label: isSmall
          ? 'Edit PLM Event Response'
          : 'Edit Peak Load Management Event Response',
        icon: <EditIcon />,
        action: () => navigate(`/plm-event-responses/${id}/edit`),
      });
    }

    setContextMenuItems([{ items: menuItems }]);
  }, [can, navigate, id, setContextMenuItems, isSmall]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case EnumPlmEventResponseResponseStatus.Completed:
        return '#4caf50';
      case EnumPlmEventResponseResponseStatus.Active:
        return '#2196f3';
      case EnumPlmEventResponseResponseStatus.Declined:
        return '#f44336';
      case EnumPlmEventResponseResponseStatus.PendingApproval:
        return '#ff9800';
      case EnumPlmEventResponseResponseStatus.Scheduled:
        return '#9c27b0';
      case EnumPlmEventResponseResponseStatus.Suspended:
        return '#ff5722';
      case EnumPlmEventResponseResponseStatus.Resumed:
        return '#00bcd4';
      default:
        return '#757575';
    }
  };

  const formatDateTime = (timestamp: number | null | undefined) => {
    if (!timestamp) return '... pending ...';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Loading PLM Event Response...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !plmEventResponse) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading PLM Event Response details
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Tooltip title="Back to List">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/plm-event-responses')}
            sx={{ mr: 2 }}
          >
            Back to PLM Event Response
          </Button>
        </Tooltip>

        {can('update', 'PlmEventResponse') && (
          <Tooltip title="Edit">
            <IconButton
              onClick={() => navigate(`/plm-event-responses/${id}/edit`)}
              sx={{ ml: 2 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={3} className="plm-grid-container">
        {/* Basic Information */}
        <Grid item className="item-wrapper" xs={12} md={6}>
          <BorderedSection title="Basic Information">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          ID:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEventResponse._id}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Event ID:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEventResponse.eventId}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Curtailment Plan ID:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEventResponse.curtailmentPlanId}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <Typography
                      className="label"
                      component="div"
                      sx={{ marginLeft: '10px' }}
                    >
                      Response Status:
                    </Typography>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Chip
                          label={humanize(`${plmEventResponse.responseStatus}`)}
                          sx={{
                            backgroundColor: getStatusColor(
                              plmEventResponse.responseStatus,
                            ),
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            minWidth: '150px',
                          }}
                        />
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        {/* Timestamps */}
        <Grid item className="item-wrapper" xs={12} md={6}>
          <BorderedSection title="Timestamps">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Created At:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.createdAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Updated At:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.updatedAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Response Sent At:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.responseSentAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        {/* Scheduled Times */}
        <Grid item className="item-wrapper" xs={12} md={6}>
          <BorderedSection title="Scheduled Times">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Scheduled Start:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.scheduledStart)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Scheduled End:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.scheduledEnd)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        {/* Actual Times */}
        <Grid item className="item-wrapper" xs={12} md={6}>
          <BorderedSection title="Actual Times">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Actual Start:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.actualStart)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Actual End:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDateTime(plmEventResponse.actualEnd)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        {/* Related Information */}
        {plmEventResponse.event && (
          <Grid item className="item-wrapper" xs={12} md={6}>
            <BorderedSection title="Related Event">
              <Grid container spacing={2} className="plm-inner-grid-container">
                <Grid item className="item-wrapper" xs={12}>
                  <List className="plm-ul" dense={false}>
                    <ListItem>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={
                          <Typography className="label" component="div">
                            Event ID:
                          </Typography>
                        }
                        secondary={
                          <Typography className="sub-label" component="div">
                            {plmEventResponse.event._id}
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={
                          <Typography className="label" component="div">
                            Contract ID:
                          </Typography>
                        }
                        secondary={
                          <Typography className="sub-label" component="div">
                            {plmEventResponse.event.contractId}
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={
                          <Typography className="label" component="div">
                            Program ID:
                          </Typography>
                        }
                        secondary={
                          <Typography className="sub-label" component="div">
                            {plmEventResponse.event.programId}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </BorderedSection>
          </Grid>
        )}

        {plmEventResponse.curtailmentPlan && (
          <Grid item className="item-wrapper" xs={12} md={6}>
            <BorderedSection title="Related Curtailment Plan">
              <Grid container spacing={2} className="plm-inner-grid-container">
                <Grid item className="item-wrapper" xs={12}>
                  <List className="plm-ul" dense={false}>
                    <ListItem>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={
                          <Typography className="label" component="div">
                            Plan ID:
                          </Typography>
                        }
                        secondary={
                          <Typography className="sub-label" component="div">
                            {plmEventResponse.curtailmentPlan._id}
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={
                          <Typography className="label" component="div">
                            Organization ID:
                          </Typography>
                        }
                        secondary={
                          <Typography className="sub-label" component="div">
                            {plmEventResponse.curtailmentPlan.organizationId}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </BorderedSection>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
